import { Switch, Route } from "wouter";
import { lazy, Suspense, useEffect, useState, useLayoutEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import ServerUnavailable from "@/components/ServerUnavailable";
import { publicPages } from "./public-pages";

const AuthPage = lazy(() => import("@/pages/auth-page"));
const Landing = publicPages["/"].Component;
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const VerifyEmailChangePage = lazy(() => import("@/pages/VerifyEmailChangePage"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const Pricing = publicPages["/pricing"].Component;
const PublicFAQ = publicPages["/faq"].Component;
const Blog = publicPages["/blog"].Component;
const BlogPost = publicPages["/blog/:slug"].Component;
const PrivacyPolicy = publicPages["/privacy"].Component;
const TermsOfService = publicPages["/terms"].Component;
const ContactUs = publicPages["/contact"].Component;
const ScheduleCall = publicPages["/schedule-a-call"].Component;
const LeadForm = publicPages["/lead-form"].Component;
const LeadFormConfirmation = lazy(() => import("@/pages/LeadFormConfirmation"));
const StaffCareKit = publicPages["/staff-care-kit"].Component;
const StaffCareKitConfirmation = lazy(() => import("@/pages/StaffCareKitConfirmation"));
const SMSConsent = publicPages["/sms-consent"].Component;
const SchoolsLanding = publicPages["/schools"].Component;
const HRLanding = publicPages["/hr"].Component;
const AgenciesLanding = publicPages["/agencies"].Component;
const ConstructionLanding = publicPages["/construction"].Component;
const BusinessLanding = publicPages["/business"].Component;
const CoachesLanding = publicPages["/coaches"].Component;
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import TrialExpired from "@/components/TrialExpired";
import PaymentGracePeriodBanner from "@/components/PaymentGracePeriodBanner";
import { useAuth, AuthProvider } from "@/contexts/AuthContext";
import { RoleLabelsProvider } from "@/contexts/RoleLabelsContext";
import { DialogLayerProvider, DialogOverlayProvider } from "@/components/ui/dialog-context";
import { useLocation } from "wouter";
import {
  hasSupervisorAccess,
  hasSaasReadAccess,
  hasSaasWriteAccess,
  isPlatformSuperAdmin,
  isSaasOnlyUser,
} from "@/lib/utils";
import { useAnalytics } from "@/hooks/use-analytics";
import PublicPageTracker from "@/components/PublicPageTracker";
import { isPrerenderRoute } from "@shared/prerender-routes";

const TeamReports = lazy(() => import("@/pages/TeamReports"));
const WeeklyReport = lazy(() => import("@/pages/WeeklyReport"));
const MyReports = lazy(() => import("@/pages/MyReports"));
const Company = lazy(() => import("@/pages/Company"));
const CompanySelector = lazy(() => import("@/pages/CompanySelector"));
const DatabaseTest = lazy(() => import("@/pages/DatabaseTest"));
const UserView = lazy(() => import("@/pages/UserView"));
const AllOrganizations = lazy(() => import("@/pages/AllOrganizations"));
const SaasSettings = lazy(() => import("@/pages/SaasSettings"));
const BackupManagement = lazy(() => import("@/pages/BackupManagement"));
const ProductionLogs = lazy(() => import("@/pages/ProductionLogs"));
const AnalyticsDashboard = lazy(() => import("@/pages/AnalyticsDashboard"));
const ReferralPartners = lazy(() => import("@/pages/ReferralPartners"));
const ReferralDashboard = lazy(() => import("@/pages/ReferralDashboard"));
const AccountSettings = lazy(() => import("@/pages/AccountSettings"));
const SecuritySettings = lazy(() => import("@/pages/SecuritySettings"));
const NewFeatures = lazy(() => import("@/pages/NewFeatures"));
const HelpSupport = lazy(() => import("@/pages/HelpSupport"));
const GettingStarted = lazy(() => import("@/pages/help/GettingStarted"));
const Documentation = lazy(() => import("@/pages/help/Documentation"));
const FAQ = lazy(() => import("@/pages/help/FAQ"));
const ReportBug = lazy(() => import("@/pages/ReportBug"));
const RequestFeature = lazy(() => import("@/pages/RequestFeature"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>
  );
}

function ScrollToTop() {
  const [location] = useLocation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return null;
}

function DashboardHome() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (user) {
      // SaaS-only users (any SaaS access without a company) land on the SaaS dashboard
      if (isSaasOnlyUser(user)) {
        navigate("/all-organizations");
        return;
      }
      // Redirect based on company role
      if (user.role === "user") {
        navigate("/my-reports");
      } else if (hasSupervisorAccess(user)) {
        navigate("/team-reports");
      }
    }
  }, [user, navigate]);

  // Show loading while redirecting
  return <div className="min-h-screen flex items-center justify-center">Redirecting...</div>;
}

function ProtectedRoute({
  component: Component,
  adminOnly = false,
  supervisorOrAbove = false,
  userOnly = false,
  superAdminOnly = false,
  saasReadOnly = false,
  saasWriteOnly = false,
  ...rest
}: any) {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    window.location.href = "/auth";
    return null;
  }

  if (superAdminOnly && !isPlatformSuperAdmin(user)) {
    return <NotFound />;
  }

  if (saasReadOnly && !hasSaasReadAccess(user)) {
    return <NotFound />;
  }

  if (saasWriteOnly && !hasSaasWriteAccess(user)) {
    return <NotFound />;
  }

  if (adminOnly && user?.role !== "admin" && user?.role !== "owner" && !user?.isSuperAdmin) {
    return <NotFound />;
  }

  if (supervisorOrAbove && user?.role === "user") {
    return <NotFound />;
  }

  if (userOnly && user?.role !== "user") {
    return <NotFound />;
  }

  return <Component {...rest} />;
}

function Router() {
  const { isAuthenticated, isLoading } = useAuth();
  const [trialExpired, setTrialExpired] = useState<any>(null);
  const [gracePeriod, setGracePeriod] = useState<any>(null);
  const [location] = useLocation();

  useAnalytics();

  useEffect(() => {
    const handleTrialExpired = (event: any) => {
      setTrialExpired(event.detail);
    };

    const handleTrialCleared = () => {
      setTrialExpired(null);
    };

    const handleGracePeriod = (event: any) => {
      setGracePeriod(event.detail);
    };

    const handleGracePeriodCleared = () => {
      setGracePeriod(null);
    };

    window.addEventListener("trialExpired", handleTrialExpired);
    window.addEventListener("trialCleared", handleTrialCleared);
    window.addEventListener("paymentGracePeriod", handleGracePeriod);
    window.addEventListener("paymentGracePeriodCleared", handleGracePeriodCleared);

    return () => {
      window.removeEventListener("trialExpired", handleTrialExpired);
      window.removeEventListener("trialCleared", handleTrialCleared);
      window.removeEventListener("paymentGracePeriod", handleGracePeriod);
      window.removeEventListener("paymentGracePeriodCleared", handleGracePeriodCleared);
    };
  }, []);

  // Public content must not wait on the session request. Private routing still does.
  if (isLoading && !isPrerenderRoute(location)) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const safeRoutes = [
    "/pricing",
    "/checkout-success",
    "/auth",
    "/signup",
    "/verify-email",
    "/verify-email-change",
    "/reset-password",
    "/privacy",
    "/terms",
    "/contact",
    "/schedule-a-call",
    "/lead-form",
    "/lead-form-confirmation",
    "/staff-care-kit",
    "/sms-consent",
    "/blog",
    "/schools",
    "/hr",
    "/agencies",
    "/construction",
    "/business",
    "/coaches",
  ];
  const isOnSafeRoute = safeRoutes.some(
    (route) =>
      location === route || location.startsWith(route + "/") || location.startsWith(route + "?"),
  );

  if (trialExpired && !isOnSafeRoute && !isPrerenderRoute(location)) {
    return (
      <TrialExpired
        reason={trialExpired.reason}
        trialEndsAt={trialExpired.trialEndsAt}
        subscriptionStatus={trialExpired.subscriptionStatus}
        companyName={trialExpired.companyName}
        onDismiss={() => setTrialExpired(null)}
      />
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <ScrollToTop />
      {!isLoading && <PublicPageTracker />}
      {gracePeriod && (
        <PaymentGracePeriodBanner
          daysRemaining={gracePeriod.daysRemaining}
          gracePeriodEndsAt={gracePeriod.gracePeriodEndsAt}
          companyName={gracePeriod.companyName}
          onDismiss={() => setGracePeriod(null)}
        />
      )}
      <Switch>
        <Route path="/auth">
          <AuthPage />
        </Route>

        {/* Reset password route - public access */}
        <Route path="/reset-password/:token">
          {(params) => <ResetPassword token={params.token} />}
        </Route>

        {/* Signup route - public access */}
        <Route path="/signup" component={SignupPage} />

        {/* Email verification route - public access */}
        <Route path="/verify-email" component={VerifyEmailPage} />

        {/* Email change verification route - public access */}
        <Route path="/verify-email-change" component={VerifyEmailChangePage} />

        {/* Pricing page - public access */}
        <Route path="/pricing" component={Pricing} />

        {/* Public marketing FAQ - public access */}
        <Route path="/faq" component={PublicFAQ} />

        {/* Public blog - public access */}
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug">{(params) => <BlogPost slug={params.slug} />}</Route>

        {/* Legal pages - public access */}
        <Route path="/privacy" component={PrivacyPolicy} />
        <Route path="/terms" component={TermsOfService} />
        <Route path="/contact" component={ContactUs} />
        <Route path="/schedule-a-call" component={ScheduleCall} />
        <Route path="/lead-form" component={LeadForm} />
        <Route path="/lead-form-confirmation" component={LeadFormConfirmation} />
        <Route path="/staff-care-kit/thank-you" component={StaffCareKitConfirmation} />
        <Route path="/staff-care-kit" component={StaffCareKit} />
        <Route path="/sms-consent" component={SMSConsent} />

        {/* Vertical landing pages - public access */}
        <Route path="/schools" component={SchoolsLanding} />
        <Route path="/hr" component={HRLanding} />
        <Route path="/agencies" component={AgenciesLanding} />
        <Route path="/construction" component={ConstructionLanding} />
        <Route path="/business" component={BusinessLanding} />
        <Route path="/coaches" component={CoachesLanding} />

        {/* Checkout success page - public access */}
        <Route path="/checkout-success" component={CheckoutSuccess} />

        {/* Company selector - authenticated but without layout */}
        {isAuthenticated && (
          <Route
            path="/select-company"
            component={() => <ProtectedRoute component={CompanySelector} />}
          />
        )}

        {/* Public routes (unauthenticated users) */}
        {!isAuthenticated && <Route path="/" component={Landing} />}

        {/* Protected routes (authenticated users) */}
        {isAuthenticated && (
          <Route>
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow bg-gray-50">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
                  <Switch>
                    <Route
                      path="/"
                      component={() => <ProtectedRoute component={DashboardHome} />}
                    />
                    <Route
                      path="/team-reports"
                      component={() => <ProtectedRoute component={TeamReports} supervisorOrAbove />}
                    />
                    <Route
                      path="/my-reports"
                      component={() => <ProtectedRoute component={MyReports} />}
                    />
                    <Route
                      path="/weekly-report"
                      component={() => <ProtectedRoute component={WeeklyReport} />}
                    />
                    <Route
                      path="/account"
                      component={() => <ProtectedRoute component={AccountSettings} />}
                    />
                    <Route
                      path="/security"
                      component={() => <ProtectedRoute component={SecuritySettings} />}
                    />
                    <Route
                      path="/new-features"
                      component={() => <ProtectedRoute component={NewFeatures} />}
                    />
                    <Route
                      path="/help"
                      component={() => <ProtectedRoute component={HelpSupport} />}
                    />
                    <Route
                      path="/help/getting-started"
                      component={() => <ProtectedRoute component={GettingStarted} />}
                    />
                    <Route
                      path="/help/documentation"
                      component={() => <ProtectedRoute component={Documentation} />}
                    />
                    <Route path="/help/faq" component={() => <ProtectedRoute component={FAQ} />} />
                    <Route
                      path="/report-bug"
                      component={() => <ProtectedRoute component={ReportBug} />}
                    />
                    <Route
                      path="/request-feature"
                      component={() => <ProtectedRoute component={RequestFeature} adminOnly />}
                    />
                    <Route
                      path="/company"
                      component={() => <ProtectedRoute component={Company} supervisorOrAbove />}
                    />
                    <Route
                      path="/all-organizations"
                      component={() => <ProtectedRoute component={AllOrganizations} saasReadOnly />}
                    />
                    <Route
                      path="/saas-settings"
                      component={() => <ProtectedRoute component={SaasSettings} saasReadOnly />}
                    />
                    <Route
                      path="/backups"
                      component={() => (
                        <ProtectedRoute component={BackupManagement} superAdminOnly />
                      )}
                    />
                    <Route
                      path="/production-logs"
                      component={() => <ProtectedRoute component={ProductionLogs} saasReadOnly />}
                    />
                    <Route
                      path="/analytics"
                      component={() => (
                        <ProtectedRoute component={AnalyticsDashboard} saasReadOnly />
                      )}
                    />
                    <Route
                      path="/user/:userId"
                      component={() => <ProtectedRoute component={UserView} supervisorOrAbove />}
                    />
                    <Route
                      path="/database-test"
                      component={() => <ProtectedRoute component={DatabaseTest} adminOnly />}
                    />
                    <Route
                      path="/admin/referral-partners"
                      component={() => <ProtectedRoute component={ReferralPartners} saasReadOnly />}
                    />
                    <Route
                      path="/referral-dashboard"
                      component={() => <ProtectedRoute component={ReferralDashboard} />}
                    />
                    <Route component={NotFound} />
                  </Switch>
                </div>
              </main>
              <Footer />
            </div>
          </Route>
        )}

        {/* Fallback for unauthenticated users trying to access protected routes */}
        <Route component={Landing} />
      </Switch>
    </Suspense>
  );
}

function App() {
  const [serverUnavailable, setServerUnavailable] = useState(false);

  useEffect(() => {
    const handleServerError = (_event: CustomEvent) => {
      setServerUnavailable(true);
    };

    window.addEventListener("serverUnavailable", handleServerError as EventListener);

    return () => {
      window.removeEventListener("serverUnavailable", handleServerError as EventListener);
    };
  }, []);

  const handleRetrySuccess = () => {
    setServerUnavailable(false);
    queryClient.invalidateQueries();
    window.location.reload();
  };

  if (serverUnavailable) {
    return <ServerUnavailable onRetrySuccess={handleRetrySuccess} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <DialogOverlayProvider>
        <DialogLayerProvider>
          <AuthProvider>
            <RoleLabelsProvider>
              <TooltipProvider>
                <Toaster />
                <Router />
              </TooltipProvider>
            </RoleLabelsProvider>
          </AuthProvider>
        </DialogLayerProvider>
      </DialogOverlayProvider>
    </QueryClientProvider>
  );
}

export default App;
